import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Dialog from '@radix-ui/react-dialog';
import { Plus, Settings, X } from 'lucide-react';
import { Buton, Etiket, Girdi, Iskelet, Kart, KartIcerik, toast } from '@emanetx/ui';
import { ayarSetle, ayarlariListele, type Ayar, type AyarSetIstegi } from '@emanetx/sdk';
import { istemci } from '../istemci.js';

export function SayfaAyarlar() {
  const qc = useQueryClient();
  const [yeniAcik, setYeniAcik] = useState(false);
  const [duzenleHedef, setDuzenleHedef] = useState<Ayar | null>(null);

  const liste = useQuery({
    queryKey: ['ayarlar'],
    queryFn: () => ayarlariListele(istemci),
  });

  const set = useMutation({
    mutationFn: (g: AyarSetIstegi) => ayarSetle(istemci, g),
    onSuccess: () => {
      toast.success('Ayar kaydedildi');
      void qc.invalidateQueries({ queryKey: ['ayarlar'] });
      setYeniAcik(false);
      setDuzenleHedef(null);
    },
    onError: (err: unknown) => {
      const m =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: string }).message)
          : 'Kaydedilemedi';
      toast.error(m);
    },
  });

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Ayarlar</h1>
          <p className="text-fume-500 dark:text-fume-400 mt-1 text-sm">
            Sistem ayarları (anahtar-değer). Bakım modu, KVKK versiyon, vb.
          </p>
        </div>
        <Buton onClick={() => setYeniAcik(true)}>
          <Plus size={16} /> Yeni Ayar
        </Buton>
      </header>

      {liste.isLoading ? (
        <Iskelet className="h-40" />
      ) : !liste.data || liste.data.length === 0 ? (
        <Kart>
          <KartIcerik className="py-10 text-center">
            <Settings size={28} className="text-fume-300 mx-auto" />
            <p className="text-fume-500 mt-2 text-sm">Henüz ayar yok.</p>
          </KartIcerik>
        </Kart>
      ) : (
        <div className="grid gap-3">
          {liste.data.map((a) => (
            <Kart key={a.anahtar}>
              <KartIcerik>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <code className="text-marka-700 dark:text-marka-400 font-mono text-sm font-semibold">
                        {a.anahtar}
                      </code>
                      <span className="text-fume-400 font-mono text-[10px]">
                        {new Date(a.guncellemeTarihi).toLocaleString('tr-TR', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                    {a.aciklama && <p className="text-fume-500 mt-1 text-xs">{a.aciklama}</p>}
                    <pre className="bg-fume-50 dark:bg-fume-800/40 mt-2 overflow-x-auto rounded-md p-2 text-xs">
                      {a.deger}
                    </pre>
                  </div>
                  <Buton stil="cizgili" boyut="kucuk" onClick={() => setDuzenleHedef(a)}>
                    Düzenle
                  </Buton>
                </div>
              </KartIcerik>
            </Kart>
          ))}
        </div>
      )}

      <AyarDialog
        acik={yeniAcik || !!duzenleHedef}
        onDegisim={(o) => {
          if (!o) {
            setYeniAcik(false);
            setDuzenleHedef(null);
          }
        }}
        ayar={duzenleHedef}
        onGonder={(g) => set.mutate(g)}
        calisiyor={set.isPending}
      />
    </div>
  );
}

function AyarDialog({
  acik,
  onDegisim,
  ayar,
  onGonder,
  calisiyor,
}: {
  acik: boolean;
  onDegisim: (a: boolean) => void;
  ayar: Ayar | null;
  onGonder: (g: AyarSetIstegi) => void;
  calisiyor: boolean;
}) {
  const duzenleme = !!ayar;
  const [anahtar, setAnahtar] = useState(ayar?.anahtar ?? '');
  const [deger, setDeger] = useState(ayar?.deger ?? '');
  const [aciklama, setAciklama] = useState(ayar?.aciklama ?? '');

  // ayar değiştiğinde state sync
  if (ayar && ayar.anahtar !== anahtar && !duzenleme === false) {
    // noop — açıkken zaten doğru gelir
  }

  const formGecerli = /^[a-z][a-z0-9_.-]{1,79}$/i.test(anahtar) && deger.length <= 2000;

  return (
    <Dialog.Root
      open={acik}
      onOpenChange={(o) => {
        if (!o) onDegisim(false);
        if (o && ayar) {
          setAnahtar(ayar.anahtar);
          setDeger(ayar.deger);
          setAciklama(ayar.aciklama ?? '');
        }
        if (o && !ayar) {
          setAnahtar('');
          setDeger('');
          setAciklama('');
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="bg-fume-900/50 fixed inset-0 z-40 backdrop-blur-sm" />
        <Dialog.Content className="dark:bg-fume-900 fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-[95vw] max-w-xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
          <div className="mb-4 flex items-center justify-between">
            <Dialog.Title className="text-lg font-semibold">
              {duzenleme ? 'Ayarı Düzenle' : 'Yeni Ayar'}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button aria-label="Kapat" className="text-fume-400 hover:text-fume-700">
                <X size={18} />
              </button>
            </Dialog.Close>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (formGecerli && !calisiyor) {
                onGonder({ anahtar, deger, aciklama: aciklama || null });
              }
            }}
            className="space-y-3"
          >
            <div>
              <Etiket htmlFor="anahtar">Anahtar</Etiket>
              <Girdi
                id="anahtar"
                value={anahtar}
                onChange={(e) => setAnahtar(e.target.value)}
                disabled={duzenleme}
                required
                placeholder="ornek: bakim.aktif"
                className="font-mono"
              />
            </div>
            <div>
              <Etiket htmlFor="deger">Değer</Etiket>
              <textarea
                id="deger"
                value={deger}
                onChange={(e) => setDeger(e.target.value)}
                rows={4}
                className="border-fume-200 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-50 mt-1 w-full rounded-lg border bg-white px-3 py-2 font-mono text-sm"
              />
            </div>
            <div>
              <Etiket htmlFor="ac">Açıklama (opsiyonel)</Etiket>
              <Girdi
                id="ac"
                value={aciklama}
                onChange={(e) => setAciklama(e.target.value)}
                placeholder="Bu ayar ne yapar?"
              />
            </div>

            <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
              <Dialog.Close asChild>
                <Buton stil="cizgili" boyut="orta" disabled={calisiyor}>
                  İptal
                </Buton>
              </Dialog.Close>
              <Buton type="submit" disabled={!formGecerli || calisiyor}>
                {calisiyor ? 'Kaydediliyor...' : 'Kaydet'}
              </Buton>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
